import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, Grid, TextField, DialogActions, Button, Box, Typography, Checkbox, FormControlLabel } from '@mui/material';
import request from '../../../helpers/myAxios'
import i18next from 'i18next';

import { Trans } from 'react-i18next';
import { useNotify } from '../../Notify/NotifyProvider';
import { useEffect } from 'react';
import { User } from '../../../types/User';
import { makeStyles } from '../../hooks/makeStyles';

const useStyles = makeStyles()(() => ({
    root: {
        width: '100%',
        paddingTop: '10px'
    },
    search: {
        flex: 'auto',
        display: 'flex',
        padding: '8px',
        alignItems: 'center',
        justifyContent: 'flex-end'
    },
    filterButton: {
        marginLeft: '4px',
        marginRight: '4px'
    },
    datePicker: {
        padding: '0px',
        margin: '0px'
    },
    generalFields: {
        paddingTop: '8px'
    },
    customFields: {
        overflow: 'auto',
        height: '300px',
        width: '100%'
    },
    groupHeader: {
        padding: '0px'
    },
    locationGroup: {
        paddingTop: '67px'
    }
}));

export interface UserEditorModalProps {
    open?: boolean,
    onSave?: (object: User) => void,
    onAbort?: () => void,
    editUser?: User | null
}

interface IFormData {
    id: string,
    email: string,
    username: string,
    first_name: string,
    last_name: string,
    newPassword: string,
    newPasswordConfirm: string
}

const UserEditorModal = (props: UserEditorModalProps) => {
    const { classes } = useStyles();
    const notify = useNotify();
    const emptyFormData: IFormData = {
        id: '', email: '', username: '', newPassword: '', newPasswordConfirm: '', first_name: '', last_name: ''

    }

    const [errors, setErrors] = useState<string[]>([]);

    const [formData, setFormData] = useState<IFormData>(emptyFormData);

    const isEdition = (props.editUser) ? true : false;

    const loadUser = (user: User) => {
        if (user.id) {
            const formData: IFormData = {
                id: user.id,
                email: user.email,
                username: user.username ? user.username : '',
                first_name: user.first_name,
                last_name: user.last_name,
                newPassword: '',
                newPasswordConfirm: ''
            };
            setFormData(formData);
        }

    }

    useEffect(() => {
        if (props.editUser)
            loadUser(props.editUser);
        else
            handleClear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props])

    const handleClear = () => {
        setErrors([]);
        setFormData(emptyFormData);
    }

    const handleAbort = () => {
        if (props.onAbort)
            props.onAbort();
        handleClear();
    }

    const handleSubmit = (e: React.FormEvent | null) => {
        const userToPost: User = {
            email: formData.email,
            first_name: formData.first_name,
            last_name: formData.last_name,
            username: formData.username
        };

        if (formData.id)
            userToPost.id = formData.id;

        if (formData.newPassword)
            userToPost.newPassword = formData.newPassword;

        let newErrors: string[] = [];
        if (!formData.username)
            newErrors.push('username');
        if (!formData.email)
            newErrors.push('email');
        if ((formData.newPassword && formData.newPassword !== formData.newPasswordConfirm) || (!formData.newPassword && !props.editUser)) {
            newErrors.push('newPassword');
            newErrors.push('newPasswordConfirm');
        }

        if (newErrors.length) {
            setErrors(newErrors);
            return;
        }

        request()[isEdition ? 'put' : 'post']('/user', { user: userToPost }).then((res) => {
            let createdUser = userToPost;
            if (!createdUser.id) {
                createdUser.id = res.data.id;
            }
            if (props.onSave)
                props.onSave(createdUser);

            notify.success(i18next.t(isEdition ? 'UserEditor.msgUserModified' : 'UserEditor.msgUserCreated'));
            handleClear();

        }).catch((err) => {
            if (err?.response?.data?.error === 'Barcode already in use')
                notify.error(i18next.t('UserEditor.msgBarcodeInUse'));
            else
                notify.error(err.message);
        });

        if (e)
            e.preventDefault();
    }

    const handleChange = (event: any) => {
        const newFormData: any = { ...formData };
        switch (event.target.id) {
            case 'email':
            case 'username':
            case 'newPassword':
            case 'newPasswordConfirm':
            case 'first_name':
            case 'last_name':
                newFormData[event.target.id] = event.target.value;
                break;
            default:
                newFormData.customFields[event.target.id] = event.target.value;
                break;
        }
        setFormData(newFormData);
    }

    const handleStatusCheckChange = (event: any) => {
        const newFormData: any = { ...formData };
        newFormData.status = event.target.checked ? 'enabled' : 'disabled';
        setFormData(newFormData);
    }

    const isError = (componentId: string, isRequired: boolean) => {
        if (isRequired)
            return errors.includes(componentId);
        return false;
    }

    return (
        <Dialog open={props.open ? true : false} fullWidth maxWidth='md'>
            <DialogTitle id="form-dialog-title">{isEdition ? <Trans i18nKey='UserEditor.titleEditUser' /> : <Trans i18nKey='UserEditor.titleCreateUser' />}</DialogTitle>
            <DialogContent className={classes.root}>
                <Grid component="form" container spacing={1} alignItems='flex-start' justifyContent="center">
                    <Grid container item xs={8}>
                        <Grid item xs={12}>
                            <Typography className={classes.groupHeader} variant='overline' color='primary'><Trans i18nKey='UserEditor.general' /></Typography>
                            <Grid container spacing={1} className={classes.generalFields}>
                                <Grid item xs={12}>
                                    <TextField id="first_name" fullWidth label={i18next.t('UserEditor.first_name')} size='small'
                                        value={formData.first_name} onChange={handleChange} variant="filled" required error={isError('first_name', true)} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField id="last_name" fullWidth label={i18next.t('UserEditor.last_name')} size='small'
                                        value={formData.last_name} onChange={handleChange} variant="filled" required error={isError('last_name', true)} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField id="username" fullWidth label={i18next.t('UserEditor.username')} size='small'
                                        value={formData.username} onChange={handleChange} variant="filled" required />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField id="newPassword" type='password' fullWidth label={i18next.t('UserEditor.newPassword')} size='small'
                                        value={formData.newPassword} onChange={handleChange} variant="filled" required={props.editUser ? false : true} error={isError('newPassword', true)} />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField id="newPasswordConfirm" type='password' fullWidth label={i18next.t('UserEditor.newPasswordConfirm')} size='small'
                                        value={formData.newPasswordConfirm} onChange={handleChange} variant="filled" required={props.editUser ? false : true} error={isError('newPasswordConfirm', true)} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField id="email" fullWidth label={i18next.t('UserEditor.email')} size='small'
                                        value={formData.email} onChange={handleChange} variant="filled" required error={isError('email', true)} />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant='contained' color='primary' onClick={handleSubmit}>{isEdition ? <Trans i18nKey='buttons.edit' /> : <Trans i18nKey='buttons.create' />}</Button>
                <Button variant='outlined' color='primary' onClick={handleAbort}><Trans i18nKey='buttons.cancel' /></Button>
            </DialogActions>
        </Dialog >
    )
}

export default UserEditorModal